import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { useEffect, useState } from 'react';
import useIcons from '../../hooks/useIcons';
import useMediaQuery from '../../hooks/useMediaQuery';
import useURLSearchParams from '../../hooks/useURLSearchParams';
import { sentenceCase } from '../../utils';
import { BtnContainer, ButtonsContainer, ServiceButton, ToolBarString, ToolsBarContainer, ToolsContainer, VerticalDivider } from './styles';

const ToolBar = ({
  toolBarButtons,
  urlSearchParam,
  onSearchChange,
  displayTools = true,
  displayAll = true,
  selectOnlyOne = false,
  displayDivider = true,
  defaultValue = '',
  middleAction,
  startAction,
  padding,
  mobilePadding,
  customizedToolComponent = () => {},
  displayString,
  mobileMiddleAction,
  noMobileBoxShadow = false
}: {
  toolBarButtons?: { name: string; id: string; onClick?: (tab: string) => void; divider?: boolean }[];
  urlSearchParam: string;
  onSearchChange?: (value: string) => void;
  displayTools?: boolean;
  displayAll?: boolean;
  selectOnlyOne?: boolean;
  displayDivider?: boolean;
  defaultValue?: string;
  middleAction?: JSX.Element | null;
  startAction?: JSX.Element | null;
  padding?: string;
  mobilePadding?: string;
  customizedToolComponent?: JSX.Element;
  displayString?: string | JSX.Element;
  mobileMiddleAction?: JSX.Element;
  noMobileBoxShadow?: boolean;
}) => {
  const { t } = useTranslation();
  const { mobile } = useMediaQuery({ mobile: true });
  const icons = useIcons();
  const searchIcon = icons?.searchBlackSVG?.publicURL;

  const [selected, setSelected] = useURLSearchParams(urlSearchParam, defaultValue || '');

  const [showServices, setShowServices] = useState<boolean>(true);
  const [showSearchInput, setShowSearchInput] = useState<boolean>(false);

  const isAllSelected = !selected || selected === '';

  const handleClickOnService = (name: string) => {
    const selectedService = selected?.toLowerCase();
    const normalizedName = name.toLowerCase();

    if (selectedService === normalizedName) {
      setSelected('');
    } else {
      setSelected(normalizedName);
    }
  };

  const handleClickAllServices = () => {
    setSelected('');
  };

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue?.toLowerCase());
    }
  }, [defaultValue]);

  return (
    <>
      <ToolsBarContainer padding={padding} mobilePadding={mobilePadding} noMobileBoxShadow={noMobileBoxShadow}>
        {showSearchInput && onSearchChange && <SearchBar onSearch={onSearchChange} showAlways />}
        {mobile && displayTools && onSearchChange && (
          <MenuButton
            onClick={() => {
              setShowServices(!showServices);
              setShowSearchInput(!showSearchInput);
            }}
            bgImage={searchIcon}
            width="28px"
            height="28px"
            mobileDisplayIcon
            style={{ minWidth: '28px' }}
          />
        )}
        {mobile && mobileMiddleAction}
        {!showSearchInput && !displayString && (
          <ButtonsContainer isVisible={showServices}>
            {displayAll && (
              <BtnContainer>
                <ServiceButton isSelected={isAllSelected} onClick={handleClickAllServices}>
                  {t('toolbar.allButton')}
                </ServiceButton>
                {displayDivider && <VerticalDivider />}
              </BtnContainer>
            )}

            {toolBarButtons?.map(({ name, id, divider, onClick }, index) => {
              return (
                <ServiceButton
                  key={id}
                  isSelected={selected.toLowerCase() === name.toLowerCase()}
                  onClick={() => {
                    if (onClick) {
                      onClick(name);
                    }
                    handleClickOnService(name);
                  }}
                >
                  {sentenceCase(name)}
                </ServiceButton>
              );
            })}
          </ButtonsContainer>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          {startAction}
          {displayString && !showSearchInput && <ToolBarString>{displayString}</ToolBarString>}
        </div>

        {displayTools && !mobile && (
          <ToolsContainer>
            {middleAction}
            {customizedToolComponent}
          </ToolsContainer>
        )}
      </ToolsBarContainer>
    </>
  );
};

export default ToolBar;
